import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Search, AlertTriangle, Clock, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_COLORS = {
  new: 'bg-sky-100 text-sky-700 border-sky-200',
  open: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  in_progress: 'bg-violet-100 text-violet-700 border-violet-200',
  pending_customer: 'bg-amber-100 text-amber-700 border-amber-200',
  resolved: 'bg-slate-100 text-slate-700 border-slate-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
  cancelled: 'bg-rose-50 text-rose-600 border-rose-200',
};
const PRIORITY_COLORS = {
  1: 'bg-rose-100 text-rose-700 border-rose-200',
  2: 'bg-orange-100 text-orange-700 border-orange-200',
  3: 'bg-blue-100 text-blue-700 border-blue-200',
  4: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function CsmTicketsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const filters = {
    status: params.get('status') || 'active',
    queue_id: params.get('queue_id') || '',
    priority: params.get('priority') || '',
    assigned_agent_id: params.get('assigned_agent_id') || '',
    breached: params.get('breached') === 'true',
  };
  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === '' || value === false) next.delete(key);
    else next.set(key, String(value));
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      if (search.trim()) q.set('search', search.trim());
      const r = await axios.get(`${API_URL}/api/csm/tickets?${q}`, { headers: authHeader() });
      setTickets(r.data.tickets || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, search]);

  useEffect(() => {
    axios.get(`${API_URL}/api/csm/queues`, { headers: authHeader() }).then(r => setQueues(r.data.queues || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4" data-testid="csm-tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]">Tickets</h1>
          <p className="text-sm text-[#64748B] mt-1">{tickets.length} ticket{tickets.length !== 1 ? 's' : ''}</p>
        </div>
        <Link to="/admin/csm" className="text-sm text-[#0E7490] hover:underline">← Back to workspace</Link>
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-[#64748B]" />
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
                className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-status">
          <option value="active">All active</option>
          <option value="new">New</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="pending_customer">Pending Customer</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={filters.queue_id} onChange={e => setFilter('queue_id', e.target.value)}
                className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-queue">
          <option value="">All queues</option>
          {queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
        </select>
        <select value={filters.priority} onChange={e => setFilter('priority', e.target.value)}
                className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-priority">
          <option value="">All priorities</option>
          <option value="1">P1 Critical</option>
          <option value="2">P2 High</option>
          <option value="3">P3 Moderate</option>
          <option value="4">P4 Low</option>
        </select>
        <select value={filters.assigned_agent_id} onChange={e => setFilter('assigned_agent_id', e.target.value)}
                className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-assignee">
          <option value="">Any assignee</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-[#64748B]">
          <input type="checkbox" checked={filters.breached} onChange={e => setFilter('breached', e.target.checked)} data-testid="filter-breached" />
          SLA breached only
        </label>
        <div className="ml-auto flex items-center gap-2 min-w-[220px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <Input placeholder="Search ticket / customer…" value={search} onChange={e => setSearch(e.target.value)}
                 className="h-8 text-sm" data-testid="search-input" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-[#E2E8F0] text-[#64748B]">No tickets match these filters.</div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr className="text-left text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                <th className="px-4 py-3">Number</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Queue</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Assignee</th>
                <th className="px-4 py-3">SLA</th>
                <th className="px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer"
                    onClick={() => navigate(`/admin/csm/tickets/${t.id}`)}
                    data-testid={`ticket-row-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#0E7490]">{t.ticket_number}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-[#0A2342]">{t.subject || t.title}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">
                    <div className="truncate">{t.created_by_name}</div>
                    {t.organization_name && <div className="text-[10px] text-[#94A3B8] truncate">{t.organization_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">{t.queue_name || <span className="text-[#94A3B8]">—</span>}</td>
                  <td className="px-4 py-3"><Badge className={`text-[10px] px-1.5 py-0 ${PRIORITY_COLORS[t.priority] || ''}`}>P{t.priority} {t.priority_label}</Badge></td>
                  <td className="px-4 py-3"><Badge className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[t.status] || ''}`}>{(t.status || '').replace('_', ' ')}</Badge></td>
                  <td className="px-4 py-3 text-xs">{t.assigned_agent_name || <span className="text-[#94A3B8]">Unassigned</span>}</td>
                  <td className="px-4 py-3"><SlaCell t={t} /></td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{t.updated_at ? new Date(t.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SlaCell({ t }) {
  if (t.sla_response_breached || t.sla_resolution_breached) {
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600" data-testid="sla-breached">
      <AlertTriangle className="w-3 h-3" /> Breached
    </span>;
  }
  const minsLeft = t.sla_resolution_due_minutes_remaining;
  if (typeof minsLeft === 'number' && t.status !== 'resolved' && t.status !== 'closed') {
    const hrs = Math.floor(Math.abs(minsLeft) / 60);
    const mins = Math.abs(minsLeft) % 60;
    const isUrgent = minsLeft < 60;
    return <span className={`inline-flex items-center gap-1 text-xs ${isUrgent ? 'text-orange-600 font-semibold' : 'text-[#64748B]'}`}>
      <Clock className="w-3 h-3" /> {hrs > 0 ? `${hrs}h ` : ''}{mins}m left
    </span>;
  }
  return <span className="text-xs text-[#94A3B8]">—</span>;
}
